import React, { useRef, useState } from 'react'
import { useLayer } from '../../components/layers'
import { PendingButton, Failed } from '../../components/pending'
import o from '../../components/Overlay.module.css'
import styles from './PersonRecordPanel.module.css'
import iv from './InviteDialog.module.css'
import { apiJson } from '../../lib/api'
import { PERMISSION_SETS } from './PersonRecordPanel'

// Invite system user (routes/invites.js). Christoph, 2026-10-27: "popup which notes (this is for control access) -
// you put in an email address which fires an expiring link (1 day) ... on that popup i also choose their access
// level". The reply carries the link, so it is shown for copying whether or not the email went.

export interface InviteResult {
  membership_id: string
  human_id: string
  email: string
  permission_set: string
  expires_at: string
  link: string
  email_sent: boolean
  email_error?: string
}

interface Props {
  user: any
  // A result to show straight away (Resend from System users), rather than the form.
  initial?: InviteResult | null
  onClose: () => void
  onInvited: (r: InviteResult) => void
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })

export function InviteDialog({ user, initial = null, onClose, onInvited }: Props) {
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [email, setEmail] = useState('')
  const [set, setSet] = useState('REPORTING')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<InviteResult | null>(initial)
  const [copied, setCopied] = useState(false)
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = !done && !!(first || last || email)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })

  const send = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<InviteResult>(user, '/api/personnel/invites', {
        body: { firstName: first.trim(), lastName: last.trim(), email: email.trim(), permissionSet: set },
      })
      setDone(r)
      onInvited(r)
    } catch (e: any) {
      setError(e?.message || 'Could not send the invitation')
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    if (!done) return
    try {
      await navigator.clipboard.writeText(done.link)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${iv.modal}`} role="dialog" aria-label="Invite system user">
          <div className={o.head}>
            <div className={o.headText}>
              <h2 className={o.title}>{done ? (initial ? 'Invitation sent again' : 'Invitation sent') : 'Invite system user'}</h2>
              <p className={o.sub}>
                This is for CONTROL access: the office system, signed in to with Google, Microsoft, Apple or Meta. Field workers
                use the Crew app and need no invitation.
              </p>
            </div>
            <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {done ? (
            <div className={o.body}>
              <p className={iv.lead}>
                {done.email_sent ? (
                  <>
                    An email with the link is on its way to <strong>{done.email}</strong>.
                  </>
                ) : (
                  <>
                    The email to <strong>{done.email}</strong> could not be sent{done.email_error ? ` (${done.email_error})` : ''}. Send them
                    the link yourself.
                  </>
                )}{' '}
                They open it, sign in, and have <strong>{done.permission_set}</strong> access.
              </p>
              <div className={o.field}>
                <span className={o.fieldLabel}>Invitation link</span>
                <div className={iv.linkRow}>
                  <input className={`${o.input} ${iv.link}`} readOnly value={done.link} onFocus={(e) => e.currentTarget.select()} aria-label="Invitation link" />
                  <button type="button" className={o.ghost} onClick={copy}>
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
                <div className={o.fieldHint}>
                  Works once, for whoever opens it and signs in, until {when(done.expires_at)}. After that, send it again from System users.
                </div>
              </div>
            </div>
          ) : (
            <div className={o.body}>
              <div className={iv.names}>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="inv-first">
                    First name
                  </label>
                  <input id="inv-first" className={o.input} value={first} autoComplete="off" onChange={(e) => setFirst(e.target.value)} />
                </div>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="inv-last">
                    Last name
                  </label>
                  <input id="inv-last" className={o.input} value={last} autoComplete="off" onChange={(e) => setLast(e.target.value)} />
                </div>
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="inv-email">
                  Email <span className={o.req}>*</span>
                </label>
                <input
                  id="inv-email"
                  className={o.input}
                  type="email"
                  value={email}
                  autoComplete="off"
                  placeholder="name@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className={o.fieldHint}>
                  The link goes here and runs out after 1 day. Someone already in Personnel with this email is invited as themselves, and the
                  names can stay empty.
                </div>
              </div>
              <div className={o.field}>
                <span className={o.fieldLabel}>Access level</span>
                <div className={styles.sets} role="radiogroup" aria-label="Access level">
                  {PERMISSION_SETS.filter((p) => p.key !== 'SYSADMIN').map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      role="radio"
                      aria-checked={set === p.key}
                      disabled={busy}
                      className={set === p.key ? styles.setOn : styles.set}
                      onClick={() => setSet(p.key)}
                    >
                      <span className={styles.setDot} aria-hidden="true" />
                      <span className={styles.setBody}>
                        <span className={styles.setKey}>{p.key}</span>
                        <span className={styles.setText}>{p.text}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className={o.foot}>
            {done ? (
              <button type="button" className={o.primary} onClick={onClose}>
                Done
              </button>
            ) : (
              <>
                <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
                  Cancel
                </button>
                <PendingButton className={o.primary} busy={busy} pending="Sending…" disabled={!email.trim()} onClick={send}>
                  Send invitation
                </PendingButton>
              </>
            )}
          </div>
          <Failed text={error} />
        </div>
      </div>
      {confirm}
    </>
  )
}
